// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xHeaderUpdate } from "@electrowire/App/Handlers/XNG/Header";
import { CustomAppState } from "@electrowire/App/Store/Reducers";
import { ButtonPresentationProps } from "@gcg/MobiusOverrides/Button";
import Close from "@gcg/XDL/Icons/Close";
import { color, xScreen } from "@gcg/XDL/Utilities";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { CSSTransition } from "react-transition-group";
import { css } from "styled-components";

const enum fields {
    globalMessage = "globalMessage",
    backgroundColor = "backgroundColor",
    // for "X" and text color
    color = "color",
    // for min-height for div
    height = "height",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.globalMessage]: string;
        [fields.height]: number;
        [fields.backgroundColor]: string;
        [fields.color]: string;
    };
}

type Props = OwnProps;

export interface GlobalWebsiteMessageStyles {
    container?: InjectableCss;
    wrapper?: InjectableCss;
    closeClickable?: ButtonPresentationProps;
    closeIcon?: IconPresentationProps;
    message?: TypographyPresentationProps;
}

export const globalWebsiteMessageStyles: GlobalWebsiteMessageStyles = {
    container: {
        css: css`
            .my-node-enter {
                opacity: 0;
            }

            .my-node-enter-active {
                opacity: 1;
            }
            .my-node-exit {
                opacity: 1;
            }

            .my-node-exit-active {
                animation: containerAnimation 500ms ease forwards;
                transform-origin: left;
            }

            @keyframes containerAnimation {
                from {
                    opacity: 1;
                    transform: translate(0, 0);
                }
                to {
                    opacity: 0;
                    transform: translate(0, -50px);
                }
            }
        `,
    },
    wrapper: {
        css: css`
            position: relative;
            width: 100%;
            padding: 0 42px 0 10px;
            text-align: center;
            display: flex;
            align-items: center;
            justify-content: center;

            ${({ height, backgroundColor }: any) => css`
                min-height: ${height || 42}px;

                ${backgroundColor &&
                css`
                    background: ${backgroundColor};
                `};
            `}
        `,
    },
    closeClickable: {
        css: css`
            position: absolute;
            right: 15px;
            top: 50%;
            transform: translateY(-50%);

            &:hover ${IconWrapper} {
                color: ${color("danger")};
            }
        `,
    },
    closeIcon: {
        size: 12,
        src: Close,
        css: css`
            transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;
        `,
    },
    message: {
        css: css`
            letter-spacing: 0;
            line-height: 26px;

            &,
            & > ${TypographyStyle} {
                margin-bottom: 0;
            }

            ${xScreen("lte", "sm")} {
                &,
                & > ${TypographyStyle} {
                    font-size: 14px;
                    letter-spacing: 0;
                    line-height: 18px;
                }
            }
        `,
    },
};

const styles = globalWebsiteMessageStyles;
const GlobalWebsiteMessage: React.FC<Props> = ({ fields: { backgroundColor, globalMessage, color, height } }) => {
    const dispatch = useDispatch();

    const { isSticky, globalMessageState } = useAppSelector(state => ({
        isSticky: (state as CustomAppState).xHeader.isSticky,
        globalMessageState: (state as CustomAppState).xHeader.globalMessage,
    }));

    const [showBanner, setShowBanner] = useState(globalMessageState);

    if (!globalMessage) {
        return null;
    }

    const onClose = () => {
        setShowBanner(false);
        dispatch(xHeaderUpdate({ key: "globalMessage", value: false }));
    };

    return (
        <StyledWrapper {...styles.container}>
            <CSSTransition
                in={showBanner}
                timeout={{
                    enter: 0,
                    exit: 200,
                }}
                unmountOnExit
                classNames="my-node"
            >
                <StyledWrapper
                    {...styles.wrapper}
                    {...({ backgroundColor, isSticky, height } as any)}
                    data-test-selector="global_site_message_container"
                >
                    <Clickable
                        {...styles.closeClickable}
                        onClick={onClose}
                        data-test-selector="global_site_message_close"
                    >
                        <Icon {...styles.closeIcon} color={color || "primary"} />
                    </Clickable>
                    <Typography
                        {...styles.message}
                        color={color || "common.white"}
                        data-test-selector="global_site_message"
                    >
                        {parse(globalMessage, parserOptions)}
                    </Typography>
                </StyledWrapper>
            </CSSTransition>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: GlobalWebsiteMessage,
    definition: {
        group: "Header",
        displayName: "Global Website Message",
        allowedContexts: ["Header"],
        fieldDefinitions: [
            {
                name: fields.globalMessage,
                displayName: "Global Message Content",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
                placeholder: "your content goes here",
                isRequired: true,
            },
            {
                name: fields.height,
                displayName: "Message height",
                editorTemplate: "IntegerField",
                defaultValue: 42,
                fieldType: "General",
                placeholder: "Min height for Messages",
                isRequired: true,
            },
            {
                name: fields.backgroundColor,
                displayName: "Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.color,
                displayName: "Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
